import React, { useState } from "react";
import { NavLink, Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useBrand } from "@/context/BrandContext";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { money } from "@/lib/format";
import { InstallPrompt } from "@/components/InstallPrompt";
import { ThemeToggle } from "@/components/ThemeToggle";
import { RoleBadge } from "@/components/RoleBadge";
import {
  House, Phone, ChatCircleText, ArrowsClockwise, Broadcast, DotsThreeCircle,
  Wallet as WalletIcon, Bell, SignOut, Gauge as GaugeHigh, Plus, Buildings, Sparkle, SquaresFour, Heart, Gift, DeviceMobile,
} from "@phosphor-icons/react";
import { AISalesDockProvider } from "@/components/AISalesDock";

const primaryNav = [
  { to: "/app", label: "Home", icon: House, end: true },
  { to: "/app/call", label: "Call", icon: Phone },
  { to: "/app/text", label: "Chat/Text", icon: ChatCircleText },
  { to: "/app/match", label: "Match", icon: Heart },
  { to: "/app/services", label: "Top Up", icon: ArrowsClockwise },
  { to: "/app/giftcards", label: "Gift Cards", icon: Gift },
  { to: "/app/airtime", label: "Airtime", icon: DeviceMobile },
  { to: "/app/ai-sales", label: "Assistant", icon: Sparkle },
  { to: "/app/products", label: "Products", icon: SquaresFour },
  { to: "/app/more", label: "More", icon: DotsThreeCircle },
];

export function ConsumerLayout({ children }) {
  const { user, logout, isStaff, isBusiness } = useAuth();
  const brand = useBrand();
  const navigate = useNavigate();
  const [menu, setMenu] = useState(false);
  const [phoneBannerDismissed, setPhoneBannerDismissed] = useState(false);

  const { data: wallet } = useQuery({
    queryKey: ["wallet"],
    queryFn: async () => (await api.get("/wallet")).data,
  });
  const { data: unread } = useQuery({
    queryKey: ["chat-unread"],
    queryFn: async () => (await api.get("/chat/unread")).data,
    refetchInterval: 15000,
  });
  const unreadCount = unread?.unread || 0;

  return (
    <AISalesDockProvider>
    <div className="min-h-screen bg-background pb-24 md:pb-0">
      {/* top bar */}
      <header className="glass sticky top-0 z-40 border-b border-black/5">
        <div className="mx-auto flex max-w-6xl items-center gap-4 px-4 py-3 md:px-8">
          <Link to="/" data-testid="brand-home" aria-label={`${brand.brand_name} — Home`} title={`${brand.brand_name} — Home`} className="flex items-center gap-2">
            <span className="inline-flex items-center gap-2 align-middle"><img src="/icons/icon-512.png" alt="iYeneh Connect" className="h-8 w-8" /><span className="text-base font-bold tracking-tight text-foreground" style={{ fontFamily: "'Outfit', sans-serif" }}>iYeneh Connect</span></span>
          </Link>

          {/* desktop nav */}
          <nav className="ml-4 hidden items-center gap-1 md:flex">
            {primaryNav.map((n) => (
              <NavLink key={n.to} to={n.to} end={n.end} data-testid={`nav-${n.label.toLowerCase().replace(" ", "-")}`}
                className={({ isActive }) =>
                  `rounded-full px-3.5 py-2 text-sm font-medium transition-colors ${
                    isActive ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-secondary"
                  }`}>
                <span className="relative">
                  {n.label}
                  {n.label === "Chat/Text" && unreadCount > 0 && (
                    <span data-testid="text-unread-badge" className="absolute -right-3 -top-1.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-primary px-1 text-[9px] font-bold text-primary-foreground">{unreadCount > 99 ? "99+" : unreadCount}</span>
                  )}
                </span>
              </NavLink>
            ))}
          </nav>

          <div className="ml-auto flex items-center gap-2">
            <button data-testid="wallet-pill" onClick={() => navigate("/app/wallet")}
              className="flex items-center gap-2 rounded-full border border-border bg-card px-3 py-1.5 transition-transform hover:scale-105">
              <WalletIcon size={16} className="text-primary" weight="duotone" />
              <span data-testid="header-wallet-balance" className="font-mono-metric text-sm font-semibold">{wallet ? money(wallet.balance_cents, wallet.currency) : "—"}</span>
              <span className="flex h-5 w-5 items-center justify-center rounded-full bg-primary text-primary-foreground"><Plus size={12} weight="bold" /></span>
            </button>
            <ThemeToggle />
            <NavLink to="/app/notifications" data-testid="nav-notifications" className="relative rounded-full p-2 text-muted-foreground hover:bg-secondary">
              <Bell size={20} weight="duotone" />
            </NavLink>
            <div className="relative">
              <button data-testid="profile-btn" onClick={() => setMenu((m) => !m)}
                className="flex h-9 w-9 items-center justify-center rounded-full bg-primary text-sm font-semibold text-primary-foreground">
                {(user?.name || "U").charAt(0).toUpperCase()}
              </button>
              {menu && (
                <div className="absolute right-0 mt-2 w-56 rounded-xl border border-border bg-card p-2 shadow-xl">
                  <div className="px-3 py-2">
                    <p className="truncate text-sm font-semibold">{user?.name}</p>
                    <p className="truncate text-xs text-muted-foreground">{user?.email}</p>
                    <div className="mt-1.5"><RoleBadge role={user?.role} pending={user?.inspector_status === "pending"} /></div>
                  </div>
                  <PresenceSelector />
                  {isStaff && (
                    <button data-testid="goto-command-center" onClick={() => navigate("/command")}
                      className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm hover:bg-secondary">
                      <GaugeHigh size={16} weight="duotone" /> CEO Command Center
                    </button>
                  )}
                  <button data-testid="goto-business-console" onClick={() => { setMenu(false); navigate("/app/business"); }}
                    className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm hover:bg-secondary">
                    <Buildings size={16} weight="duotone" /> {isBusiness ? "Business Console" : "Upgrade to Business"}
                  </button>
                  <button data-testid="profile-link" onClick={() => { setMenu(false); navigate("/app/profile"); }}
                    className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm hover:bg-secondary">Profile & Settings</button>
                  <button data-testid="logout-btn" onClick={logout}
                    className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm text-destructive hover:bg-secondary">
                    <SignOut size={16} /> Sign out
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </header>

      {user && user.phone_verified === false && !phoneBannerDismissed && (
        <div data-testid="verify-phone-banner" className="border-b border-amber-300/70 bg-amber-50 dark:border-amber-900/50 dark:bg-amber-950/30">
          <div className="mx-auto flex max-w-6xl items-center gap-3 px-4 py-2.5 text-sm md:px-8">
            <Phone size={16} weight="duotone" className="shrink-0 text-amber-600 dark:text-amber-400" />
            <span className="text-amber-800 dark:text-amber-200">Secure your account — add and verify a mobile number. It only takes a moment.</span>
            <button data-testid="verify-phone-cta" onClick={() => navigate("/app/profile")}
              className="ml-auto shrink-0 rounded-full bg-amber-500 px-3.5 py-1 text-xs font-semibold text-white hover:bg-amber-600">Add number</button>
            <button data-testid="verify-phone-dismiss" onClick={() => setPhoneBannerDismissed(true)} aria-label="Dismiss"
              className="shrink-0 rounded-full px-2 py-1 text-xs text-amber-700 hover:bg-amber-100 dark:text-amber-300 dark:hover:bg-amber-900/40">✕</button>
          </div>
        </div>
      )}

      <main className="mx-auto max-w-6xl px-4 py-6 md:px-8 md:py-10">{children}</main>

      {/* mobile bottom nav */}
      <nav className="glass fixed inset-x-0 bottom-0 z-40 border-t border-black/5 md:hidden">
        <div className="mx-auto grid max-w-md grid-cols-8">
          {primaryNav.map((n) => (
            <NavLink key={n.to} to={n.to} end={n.end} data-testid={`tab-${n.label.toLowerCase().replace(" ", "-")}`}
              className={({ isActive }) =>
                `flex flex-col items-center gap-1 py-2.5 text-[9px] font-medium transition-colors ${
                  isActive ? "text-primary" : "text-muted-foreground"
                }`}>
              <span className="relative">
                <n.icon size={20} weight={"duotone"} />
                {n.label === "Chat/Text" && unreadCount > 0 && (
                  <span className="absolute -right-2.5 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-primary px-1 text-[9px] font-bold text-primary-foreground">{unreadCount > 99 ? "99+" : unreadCount}</span>
                )}
              </span>
              {n.label}
            </NavLink>
          ))}
        </div>
      </nav>

      <InstallPrompt />
    </div>
    </AISalesDockProvider>
  );
}

const PRESENCE_META = {
  available: { label: "Available", dot: "bg-emerald-500" },
  busy: { label: "Busy", dot: "bg-red-500" },
  away: { label: "Away", dot: "bg-amber-500" },
  dnd: { label: "Do not disturb", dot: "bg-purple-500" },
  offline: { label: "Appear offline", dot: "bg-muted-foreground/50" },
};

function PresenceSelector() {
  const { data, refetch } = useQuery({ queryKey: ["my-presence"], queryFn: async () => (await api.get("/pbx/presence")).data });
  const cur = data?.status || "available";
  const change = async (s) => { try { await api.post("/pbx/presence", { status: s }); refetch(); } catch (e) {} };
  return (
    <div className="border-y border-border px-3 py-2" data-testid="presence-selector">
      <p className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">My status</p>
      <div className="grid grid-cols-1 gap-0.5">
        {Object.entries(PRESENCE_META).map(([k, m]) => (
          <button key={k} data-testid={`presence-${k}`} onClick={() => change(k)}
            className={`flex items-center gap-2 rounded-md px-2 py-1 text-xs transition-colors hover:bg-secondary ${cur === k ? "bg-secondary font-semibold" : ""}`}>
            <span className={`h-2 w-2 rounded-full ${m.dot}`} /> {m.label}
          </button>
        ))}
      </div>
    </div>
  );
}
